import React, { Component } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Box,
  IconButton
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@material-ui/icons';
import { connect } from 'react-redux';
//import { sendWhatsAppOTP, verifyWhatsAppOTPCode } from '../../services/whatsappService';
import { showToast } from '../../store/actions/toastAction';
import { signOut } from '../../store/actions/authActions';
import { LIGHTBLUE, RED } from '../../utils/color_utils';

const styles = (theme) => ({
  dialog: {
    minWidth: 400,
  },
  title: {
    backgroundColor: LIGHTBLUE,
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  content: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  otpInput: {
    fontSize: '1.5rem',
    textAlign: 'center',
    letterSpacing: '0.5rem',
    fontFamily: 'monospace',
  },
  button: {
    margin: theme.spacing(1),
  },
  verifyButton: {
    backgroundColor: LIGHTBLUE,
    color: 'white',
    '&:hover': {
      backgroundColor: LIGHTBLUE,
      opacity: 0.8,
    },
  },
  resendButton: {
    color: LIGHTBLUE,
  },
  timer: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginTop: theme.spacing(1),
  },
  whatsappIcon: {
    color: '#25D366',
    fontSize: '3rem',
    marginBottom: theme.spacing(2),
  },
});

class WhatsAppOTPDialog extends Component {
  state = {
    otp: '',
    loading: false,
    resending: false,
    timeLeft: 300, // 5 minutes in seconds
    timerInterval: null,
  };

  componentDidMount() {
    this.startTimer();
  }

  componentWillUnmount() {
    if (this.state.timerInterval) {
      clearInterval(this.state.timerInterval);
    }
  }

  startTimer = () => {
    const timerInterval = setInterval(() => {
      this.setState(prevState => {
        if (prevState.timeLeft <= 1) {
          clearInterval(timerInterval);
          return { timeLeft: 0, timerInterval: null };
        }
        return { timeLeft: prevState.timeLeft - 1 };
      });
    }, 1000);

    this.setState({ timerInterval });
  };

  formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  handleOTPChange = (event) => {
    const value = event.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      this.setState({ otp: value });
    }
  };

  handleVerifyOTP = async () => {
    const { otp } = this.state;
    const { phoneNumber, onVerificationSuccess } = this.props;

    if (otp.length !== 6) {
      this.props.showToast({
        type: 'SHOW_TOAST',
        open: true,
        variant: 'warning',
        message: 'Please enter a 6-digit OTP',
      });
      return;
    }

    this.setState({ loading: true });

    try {
      const result = await verifyWhatsAppOTPCode(phoneNumber, otp);
      
      if (result.success) {
        this.props.showToast({
          type: 'SHOW_TOAST',
          open: true,
          variant: 'success',
          message: 'OTP verified successfully!',
        });
        onVerificationSuccess();
      } else {
        this.props.showToast({
          type: 'SHOW_TOAST',
          open: true,
          variant: 'error',
          message: result.message || 'Verification failed',
        });
      }
    } catch (error) {
      this.props.showToast({
        type: 'SHOW_TOAST',
        open: true,
        variant: 'error',
        message: 'Error verifying OTP. Please try again.',
      });
    }

    this.setState({ loading: false });
  };

  handleResendOTP = async () => {
    const { phoneNumber } = this.props;
    
    this.setState({ resending: true });

    try {
      const result = await sendWhatsAppOTP(phoneNumber);
      
      if (result.success) {
        this.props.showToast({
          type: 'SHOW_TOAST',
          open: true,
          variant: 'success',
          message: 'New OTP sent to your WhatsApp!',
        });
        
        // Reset timer
        if (this.state.timerInterval) {
          clearInterval(this.state.timerInterval);
        }
        this.setState({ timeLeft: 300, otp: '' }, () => {
          this.startTimer();
        });
      } else {
        this.props.showToast({
          type: 'SHOW_TOAST',
          open: true,
          variant: 'error',
          message: result.message || 'Failed to send OTP',
        });
      }
    } catch (error) {
      this.props.showToast({
        type: 'SHOW_TOAST',
        open: true,
        variant: 'error',
        message: 'Error sending OTP. Please try again.',
      });
    }

    this.setState({ resending: false });
  };

  handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      this.handleVerifyOTP();
    }
  };

  handleGoBack = () => {
    // Just close the dialog and go back to the menu
    this.props.onClose();
    
    this.props.showToast({
      type: 'SHOW_TOAST',
      open: true,
      variant: 'info',
      message: 'WhatsApp verification cancelled. You can verify later to access MEV Exchange.',
    });
  };

  render() {
    const { classes, open, onClose } = this.props;
    const { otp, loading, resending, timeLeft } = this.state;

    return (
      <Dialog
        open={open}
        onClose={() => {}} // Disable close by clicking outside
        maxWidth="sm"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
      >
        <DialogTitle className={classes.title}>
          <Typography variant="h6">WhatsApp Verification Required</Typography>
          <IconButton onClick={this.handleGoBack} style={{ color: 'white' }} title="Go Back">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent className={classes.content}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <div className={classes.whatsappIcon}>📱</div>
            
            <Typography variant="h6" gutterBottom>
              Enter Verification Code
            </Typography>
            
            <Typography variant="body2" color="textSecondary" paragraph>
              We've sent a 6-digit verification code to your WhatsApp number.
              Please enter it below to complete your login.
            </Typography>

            <Typography variant="body2" color="textSecondary" paragraph style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
              Note: WhatsApp verification is required because you have enabled this security feature in your profile settings.
              If you don't want to verify now, you can sign out and disable this feature in your profile.
            </Typography>

            <TextField
              value={otp}
              onChange={this.handleOTPChange}
              onKeyPress={this.handleKeyPress}
              placeholder="000000"
              variant="outlined"
              margin="normal"
              inputProps={{
                maxLength: 6,
                className: classes.otpInput,
              }}
              autoFocus
              fullWidth
              disabled={loading || resending}
            />

            {timeLeft > 0 && (
              <Typography className={classes.timer}>
                Code expires in: {this.formatTime(timeLeft)}
              </Typography>
            )}

            {timeLeft === 0 && (
              <Typography color="error" className={classes.timer}>
                Code has expired. Please request a new one.
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions style={{ padding: '16px 24px', justifyContent: 'space-between' }}>
          <Button
            onClick={this.handleGoBack}
            color="secondary"
            variant="outlined"
            disabled={loading || resending}
          >
            Go Back
          </Button>
          
          <div>
            <Button
              onClick={this.handleResendOTP}
              disabled={resending || timeLeft > 240} // Allow resend after 1 minute
              startIcon={resending ? <CircularProgress size={16} /> : <RefreshIcon />}
              className={classes.resendButton}
              style={{ marginRight: '8px' }}
            >
              {resending ? 'Sending...' : 'Resend Code'}
            </Button>
            
            <Button
              onClick={this.handleVerifyOTP}
              disabled={loading || otp.length !== 6 || timeLeft === 0}
              variant="contained"
              className={classes.verifyButton}
              startIcon={loading && <CircularProgress size={16} color="inherit" />}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  showToast: (params) => dispatch(showToast(params)),
  signOut: () => dispatch(signOut()),
});

export default connect(null, mapDispatchToProps)(withStyles(styles)(WhatsAppOTPDialog)); 